import { Icon } from '@examdojo/core/icon';

export interface LibraryDataset {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly columnLabel: string;
  readonly icon: Icon;
  readonly values: readonly number[];
}

export const LIBRARY_DATASETS: readonly LibraryDataset[] = [
  // ── Normal ──────────────────────────────────────────────────────────────
  {
    id: 'student-heights',
    name: 'Student Heights',
    description: 'Heights of 17-year-old students (cm) — classic bell curve',
    columnLabel: 'Height (cm)',
    icon: 'ruler-vertical',
    values: [
      162, 175, 168, 171, 158, 180, 165, 173, 169, 177, 163, 170, 176, 160, 174, 167, 172, 178, 164, 169, 181, 166, 173,
      159, 175, 170, 168, 183, 161, 174, 167, 171, 179, 165, 172, 176, 163, 170, 185, 168, 173, 157, 177, 166, 171, 164,
      180, 169, 175, 162,
    ],
  },

  // ── Right-skewed (strong) ───────────────────────────────────────────────
  {
    id: 'household-income',
    name: 'Household Income',
    description: 'Annual household incomes ($1000s) — long right tail',
    columnLabel: 'Income ($1000s)',
    icon: 'sack-dollar',
    values: [
      35, 42, 28, 55, 38, 125, 45, 32, 65, 40, 48, 30, 95, 37, 52, 41, 75, 33, 58, 44, 29, 110, 39, 85, 47, 36, 150, 43,
      62, 31, 50, 38, 72, 46, 34, 180, 41, 55, 30, 88, 37, 60, 42, 48, 35, 105, 40, 53, 33, 68,
    ],
  },

  // ── Left-skewed ─────────────────────────────────────────────────────────
  {
    id: 'exam-scores',
    name: 'Exam Scores',
    description: 'Maths exam scores on an easy test — most score high, tail to the left',
    columnLabel: 'Score',
    icon: 'graduation-cap',
    values: [
      92, 87, 78, 95, 83, 88, 91, 76, 98, 85, 43, 89, 94, 82, 90, 73, 96, 86, 81, 93, 55, 88, 97, 84, 79, 91, 68, 87,
      95, 82, 90, 63, 85, 93, 77, 99, 88, 48, 86, 92,
    ],
  },

  // ── Exponential (extreme right-skew) ────────────────────────────────────
  {
    id: 'cafe-wait-times',
    name: 'Café Wait Times',
    description: 'Time (min) customers wait in a queue — most short, few very long',
    columnLabel: 'Minutes',
    icon: 'mug-hot',
    values: [
      0.5, 1.2, 3.8, 0.8, 2.1, 0.3, 5.2, 1.5, 0.7, 3.1, 1.8, 0.4, 7.5, 2.6, 1.1, 0.6, 4.3, 1.9, 0.9, 2.8, 0.5, 12.3,
      1.4, 3.5, 0.8, 6.1, 1.7, 0.3, 8.7, 2.2, 1.0, 4.8, 0.6, 1.3, 3.2, 0.9, 5.7, 1.6, 0.4, 2.4, 15.2, 1.1, 0.7, 3.9,
      9.8,
    ],
  },

  // ── Uniform (discrete) ─────────────────────────────────────────────────
  {
    id: 'die-rolls',
    name: 'Die Rolls',
    description: 'Rolling a fair six-sided die 60 times — roughly equal frequencies',
    columnLabel: 'Face',
    icon: 'dice',
    values: [
      3, 5, 2, 6, 1, 4, 2, 3, 5, 1, 6, 4, 2, 5, 3, 1, 6, 4, 3, 2, 5, 1, 4, 6, 3, 2, 5, 4, 1, 6, 3, 5, 2, 4, 1, 6, 3, 1,
      5, 2, 4, 6, 1, 3, 5, 2, 6, 4, 1, 3, 4, 2, 5, 6, 1, 3, 4, 2, 6, 5,
    ],
  },

  // ── Bimodal ────────────────────────────────────────────────────────────
  {
    id: 'geyser-eruptions',
    name: 'Geyser Eruptions',
    description: 'Eruption durations (min) — two distinct peaks at ~2 and ~4.5 min',
    columnLabel: 'Duration (min)',
    icon: 'volcano',
    values: [
      1.8, 4.6, 2.0, 4.3, 1.9, 4.8, 2.1, 4.5, 1.7, 4.4, 4.7, 2.0, 1.8, 4.2, 2.2, 4.6, 1.9, 4.5, 2.0, 4.1, 4.8, 1.8, 4.3,
      2.1, 4.7, 1.9, 4.4, 2.0, 4.6, 1.7, 4.5, 2.2, 4.3, 1.8, 4.9, 2.0, 4.4, 1.9, 4.7, 2.1, 3.2, 4.5, 1.8, 4.6, 2.0, 4.3,
      1.9, 4.8, 2.1, 4.4,
    ],
  },

  // ── U-shaped ───────────────────────────────────────────────────────────
  {
    id: 'app-ratings',
    name: 'App Ratings',
    description: 'Star ratings (1–5) for a polarising app — peaks at extremes',
    columnLabel: 'Stars',
    icon: 'star',
    values: [
      5, 1, 5, 1, 5, 1, 4, 5, 1, 2, 1, 5, 1, 5, 3, 1, 5, 2, 5, 1, 5, 1, 4, 5, 1, 5, 1, 2, 5, 4, 1, 5, 1, 5, 3, 1, 5, 4,
      5, 2, 5, 1, 5, 1, 5, 3, 1, 5, 2, 4, 1, 5, 1, 4, 5, 1, 5, 1, 2, 5,
    ],
  },

  // ── Roughly symmetric / uniform (continuous) ────────────────────────────
  {
    id: 'daily-temperatures',
    name: 'Daily Temperatures',
    description: 'Daily high temperatures over a month (°C)',
    columnLabel: '°C',
    icon: 'temperature-half',
    values: [
      28, 31, 27, 30, 29, 32, 28, 26, 31, 30, 29, 27, 33, 28, 30, 31, 29, 26, 32, 28, 30, 27, 29, 31, 33, 28, 30, 29,
      27, 32,
    ],
  },

  // ── Right-skewed (moderate) ─────────────────────────────────────────────
  {
    id: 'reaction-times',
    name: 'Reaction Times',
    description: 'Reaction time (ms) in a cognitive test — bulk 185–250, tail to 340',
    columnLabel: 'ms',
    icon: 'stopwatch',
    values: [
      215, 198, 242, 310, 187, 225, 268, 195, 233, 201, 278, 245, 189, 210, 255, 340, 203, 220, 192, 265, 230, 208, 290,
      197, 238, 185, 252, 212, 275, 199, 248, 305, 218, 195, 260, 205, 235, 190, 222, 280, 196, 240, 210, 320, 228, 202,
      255, 193, 245, 215,
    ],
  },

  // ── Left-skewed (subtle) ────────────────────────────────────────────────
  {
    id: 'sleep-duration',
    name: 'Sleep Duration',
    description: 'Hours of sleep per night — most students sleep 8–9 h, tail toward 5 h',
    columnLabel: 'Hours',
    icon: 'moon',
    values: [
      8.5, 9.0, 7.5, 8.0, 9.5, 8.5, 7.0, 9.0, 8.0, 8.5, 9.0, 8.0, 5.5, 8.5, 9.0, 8.0, 8.5, 9.0, 7.5, 8.0, 9.0, 6.0, 8.5,
      9.0, 8.0, 9.5, 8.5, 8.0, 6.5, 7.0, 8.0, 9.0, 8.5, 9.0, 4.5, 8.0, 9.0, 7.5, 8.5, 8.0,
    ],
  },
];
